/* ==========================================================================
   Boost Horsepower Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-bhc]');
  if (!root) return;

  var SEA_LEVEL_PSI = 14.696;

  /* Fraction of the theoretical pressure ratio gain that actually reaches the
     crank. Heat, pumping losses and a less than perfect tune all take a share. */
  var COOLING_EFF = { inter: 0.85, water: 0.88, none: 0.68 };

  /* Superchargers consume crank power to drive. Turbochargers use exhaust
     energy, which is not free either but does not show as a direct belt load. */
  var DRIVE_LOSS = { turbo: 0.00, roots: 0.12, centri: 0.08 };

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.naHp) || input.naHp <= 0) {
      return { ok: false, errors: ['Enter the naturally aspirated power.'] };
    }
    if (!isFinite(input.boost) || input.boost < 0) {
      return { ok: false, errors: ['Enter the boost pressure in psi.'] };
    }

    var alt = isFinite(input.altitude) && input.altitude > 0 ? input.altitude : 0;
    /* Standard atmosphere approximation. */
    var ambient = SEA_LEVEL_PSI * Math.pow(1 - 6.87535e-6 * alt, 5.2559);

    var eff = COOLING_EFF[input.cooling] || COOLING_EFF.inter;
    var loss = DRIVE_LOSS[input.type] || 0;

    var ratio = (ambient + input.boost) / ambient;
    var gross = input.naHp * (1 + (ratio - 1) * eff);
    var net = gross * (1 - loss);

    /* The NA figure was made at sea level, so correct it for altitude too. */
    var naAtAlt = input.naHp * (ambient / SEA_LEVEL_PSI);

    var status, warnings = [];
    if (input.boost === 0) {
      status = { key: 'good', label: num(net, 0) + ' hp', note: 'No boost. This is the naturally aspirated figure.' };
    } else if (input.boost <= 8) {
      status = { key: 'good', label: num(net, 0) + ' hp',
                 note: 'A conservative boost level that most stock bottom ends tolerate with a proper tune.' };
    } else if (input.boost <= 15) {
      status = { key: 'monitor', label: num(net, 0) + ' hp',
                 note: 'Moderate boost. Fuel system, tune and charge cooling all have to be right.' };
    } else if (input.boost <= 25) {
      status = { key: 'soon', label: num(net, 0) + ' hp',
                 note: 'High boost. Forged internals, a serious fuel system and careful tuning territory.' };
    } else {
      status = { key: 'now', label: num(net, 0) + ' hp',
                 note: 'Very high boost. Purpose built engine only.' };
    }

    if (input.cooling === 'none' && input.boost > 8) {
      warnings.push('Above about 8 psi without an intercooler, charge temperature climbs enough that you lose much of the gain and risk detonation. The efficiency figure here already reflects that, but the detonation risk is the real problem.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Estimated power', value: num(net, 0) + ' hp',
          sub: signed(net - input.naHp, 0) + ' hp over the naturally aspirated figure, a '
             + num(((net / input.naHp) - 1) * 100, 0) + '% gain.' },
        { label: 'Pressure ratio', value: num(ratio, 2) + ' : 1',
          sub: num(ambient + input.boost, 1) + ' psi absolute against ' + num(ambient, 1) + ' psi ambient.' }
      ],
      rows: [
        { head: 'Inputs' },
        { label: 'Naturally aspirated', value: comma(Math.round(input.naHp)) + ' hp' },
        { label: 'Boost', value: num(input.boost, 1) + ' psi' },
        { label: 'Ambient pressure', value: num(ambient, 2) + ' psi' },
        { label: 'Pressure ratio', value: num(ratio, 3) },
        { head: 'Losses' },
        { label: 'Charge cooling efficiency', value: num(eff * 100, 0) + '%' },
        { label: 'Drive loss', value: num(loss * 100, 0) + '%' },
        { head: 'Result' },
        { label: 'Gross', value: num(gross, 0) + ' hp' },
        { label: 'Net at the crank', value: num(net, 0) + ' hp' },
        { label: 'Gain', value: signed(net - input.naHp, 0) + ' hp' },
        { label: 'NA power at this altitude', value: num(naAtAlt, 0) + ' hp' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "naHp",
          "key": "p",
          "type": "number"
      },
      {
          "name": "boost",
          "key": "b",
          "type": "number"
      },
      {
          "name": "cooling",
          "key": "c",
          "type": "select"
      },
      {
          "name": "type",
          "key": "t",
          "type": "select"
      },
      {
          "name": "altitude",
          "key": "a",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-bhc__figure">'
        + '<div class="pc-bhc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-bhc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-bhc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-bhc__row pc-bhc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-bhc__row">'
        + '<span class="pc-bhc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-bhc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_BHC = { compute: compute };
})();
